// Terrain, a strict port of the demo's Terrain view (scrips-demo-samer `?view=terrain`, synthetic patient; Andrew 4 Nov: "match
// exactly the demo"), on the record's real data.
//   Left card: Radar | Matrix. The radar's axis names are the system switch; under it the demo's legend and "Not in the
//   radar: …" links. Matrix: an empty state (the record holds no IFM node per marker and no plan items by lever).
//   Right card: the selected system, as the demo draws it: title and status word, "x of y banded in target", "previous
//   draw", then one tile per marker (in target ones behind "Show n in target"), problem-list entries and, for Heart &
//   vessels, the PREVENT risk instrument NAMED, never computed.
// Every value comes from the record; where it has nothing the tile says "not on file". Colours are tokens only.
import { useMemo, useState } from 'react';
import { Segmented } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { V2_BODY, V2_CARD_TITLE, V2_META, V2_META_STRONG } from '@/shared/ui/design-v2';
import { TerrainRadarChart } from './terrain-radar-chart';
import { TerrainMatrix } from './terrain-matrix';
import { attentionCount, type MarkerRow, type SystemSummary } from './lib/terrain-model';
import type { TerrainReading } from './lib/terrain-model';
import type { TerrainSystemId } from './lib/terrain-catalog';

export interface TerrainCondition {
  name: string;
  system: TerrainSystemId;
}

export interface TerrainViewProps {
  systems: SystemSummary[];
  conditions: TerrainCondition[];
  isLoading?: boolean;
  isPartial?: boolean;
}

// The demo's card: white, the line token, 12 px corners, 20/24 padding.
const CARD = 'min-w-0 rounded-xl border border-border-card bg-surface-card p-5';
// The demo's marker tile: white, the line token, 8 px corners, 12/8 padding.
const TILE = 'flex w-full overflow-hidden rounded-lg border border-border-card bg-surface-card';
const SECONDARY = 'text-text-secondary';
const LINK = 'text-left text-brand-primary hover:underline';

export function formatTerrainDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatValue(r: TerrainReading): string {
  const n = (v: number) => String(Number(v.toFixed(2)));
  return r.value2 != null ? `${n(r.value)}/${n(r.value2)}` : n(r.value);
}

/** The demo's flag at the tile's right: against the band first, else the lab range. */
export function directionLine(row: MarkerRow): string | null {
  if (!row.latest) return 'not on file';
  if (row.band && row.direction) {
    if (row.band.kind === 'threshold') return row.direction === 'within' ? 'under cut-off' : row.direction === 'above' ? 'above cut-off' : 'below cut-off';
    return row.direction === 'above' ? '↑ target' : row.direction === 'below' ? '↓ target' : 'in target';
  }
  if (row.labRange === 'above') return '↑ lab range';
  if (row.labRange === 'below') return '↓ lab range';
  if (row.labRange === 'within') return 'within lab range';
  return null;
}

/** The demo's status glyph before the marker name. */
function markerGlyph(row: MarkerRow) {
  if (!row.latest) return <span aria-hidden className="text-text-muted">○</span>;
  const flagged = row.direction === 'above' || row.direction === 'below' || row.labRange === 'above' || row.labRange === 'below';
  if (flagged) return <span aria-hidden className="text-status-amber-text">▲</span>;
  return <span aria-hidden className="text-status-success">✓</span>;
}

function TerrainMarkerTile({ row }: { row: MarkerRow }) {
  const flag = directionLine(row);
  const flagged = row.direction === 'above' || row.direction === 'below' || (!row.band && (row.labRange === 'above' || row.labRange === 'below'));
  return (
    <li className={TILE} data-slot="terrain-marker" data-marker={row.marker.key} data-status={row.status}>
      <span className="min-w-0 flex-1 px-3 py-2">
        <span className={cn(V2_META, 'flex items-center gap-2', SECONDARY)}>
          {markerGlyph(row)}
          {row.marker.label}
        </span>
        <span className="flex flex-wrap items-baseline justify-between gap-x-2">
          <span className={cn(V2_CARD_TITLE, 'whitespace-nowrap tabular-nums')}>
            {row.latest ? formatValue(row.latest) : '—'}
            {row.latest ? <span className={cn(V2_META, 'ml-1', SECONDARY)}>{row.latest.unit || row.marker.unit}</span> : null}
          </span>
          {flag ? <span className={cn(V2_META_STRONG, flagged ? 'text-status-amber-text' : SECONDARY)}>{flag}</span> : null}
        </span>
        {row.latest ? (
          <span className={cn(V2_META, 'block', SECONDARY)}>
            {formatTerrainDate(row.latest.date)}
            {row.latest.reported ? ` · reported as ${row.latest.reported.value} ${row.latest.reported.unit}` : ''}
          </span>
        ) : (
          <span className={cn(V2_META, 'block', SECONDARY)}>not on file</span>
        )}
        <span className={cn(V2_META, 'block', SECONDARY)}>{row.band ? row.band.short : row.bandNote}</span>
      </span>
    </li>
  );
}

const PREVENT_INPUTS: ReadonlyArray<[string, string]> = [
  ['Total cholesterol', 'totalchol'],
  ['HDL-C', 'hdl'],
  ['Systolic blood pressure', 'bp'],
  ['BMI, as recorded', 'bmi'],
  ['eGFR', 'egfr'],
  ['HbA1c', 'hba1c'],
  ['Urine albumin/creatinine', 'uacr'],
];

function TerrainRiskInstruments({ system, all }: { system: SystemSummary; all: SystemSummary[] }) {
  const rowFor = (key: string) => all.flatMap((s) => s.rows).find((r) => r.marker.key === key);
  // Only inputs the catalog reads: a marker it does not carry is not listed, never shown as "not on file".
  const inputs = PREVENT_INPUTS.filter(([, key]) => rowFor(key));
  return (
    <section data-slot="terrain-risk" className="mt-4 flex flex-col gap-2 border-t border-border-card pt-4">
      <h3 className={cn(V2_META_STRONG, SECONDARY)}>Risk instruments · named, not computed</h3>
      <div className={TILE}>
        <div className="min-w-0 flex-1 px-3 py-2">
          <p className="flex flex-wrap items-baseline justify-between gap-x-2">
            <span className={V2_CARD_TITLE}>PREVENT</span>
            <span className={cn(V2_META, SECONDARY)}>AHA 2023</span>
          </p>
          <p className={cn(V2_META, SECONDARY)}>Adults aged 30 to 79 without known cardiovascular disease. Supersedes the Pooled Cohort Equations.</p>
          <p className={cn(V2_META, 'mt-2', SECONDARY)}>
            Whether this patient is in that population is the physician&apos;s call. Scrips does not calculate the score.
          </p>
          <ul className="mt-2 flex flex-col gap-0.5" aria-label={`${system.system.label} risk inputs`}>
            {inputs.map(([label, key]) => {
              const r = rowFor(key);
              return (
                <li key={key} className={cn(V2_META, SECONDARY)}>
                  {label} ·{' '}
                  {r?.latest ? `${formatValue(r.latest)} ${r.latest.unit || r.marker.unit} · ${formatTerrainDate(r.latest.date)}` : 'not on file'}
                </li>
              );
            })}
            <li className={cn(V2_META, SECONDARY)}>Age, sex, smoking, diabetes, BP-lowering medication, statin use · not read here</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function statusWord(s: SystemSummary) {
  if (s.state === 'attention') return <span className={cn(V2_META_STRONG, 'text-status-amber-text')}>ⓘ Needs attention</span>;
  if (s.state === 'within') return <span className={cn(V2_META_STRONG, 'text-status-success')}>✓ Within target bands</span>;
  return <span className={cn(V2_META_STRONG, SECONDARY)}>Not yet derived</span>;
}

function TerrainSystemPanel({ summary, all, conditions }: { summary: SystemSummary; all: SystemSummary[]; conditions: TerrainCondition[] }) {
  const [showWithin, setShowWithin] = useState(false);
  const withinRows = summary.rows.filter((r) => r.status === 'within');
  const shown = summary.rows.filter((r) => r.status !== 'within');
  const problems = conditions.filter((c) => c.system === summary.system.id);
  return (
    <section className={CARD} data-slot="terrain-system" data-system={summary.system.id}>
      <header className="mb-3 flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="flex min-w-0 items-center gap-2">
          <h2 className={V2_CARD_TITLE}>{summary.system.label}</h2>
          {statusWord(summary)}
        </span>
      </header>
      <div className={cn(V2_META, 'mb-3 flex flex-col', SECONDARY)}>
        {summary.banded > 0 ? (
          <span>
            <strong className="text-text-primary">
              {summary.inTarget} of {summary.banded}
            </strong>{' '}
            banded in target
          </span>
        ) : (
          <span>No banded marker</span>
        )}
        {summary.previousBanded > 0 ? (
          <span>
            previous draw{' '}
            <strong className="text-text-primary">
              {summary.previousInTarget} of {summary.previousBanded}
            </strong>
          </span>
        ) : null}
      </div>
      {shown.length ? (
        <ul className="grid gap-2">
          {shown.map((r) => (
            <TerrainMarkerTile key={r.marker.key} row={r} />
          ))}
        </ul>
      ) : null}
      {withinRows.length ? (
        <>
          <button type="button" className={cn(V2_META_STRONG, 'mt-2', LINK)} aria-expanded={showWithin} onClick={() => setShowWithin((v) => !v)}>
            {showWithin ? 'Hide markers in target' : `Show ${withinRows.length} in target`}
          </button>
          {showWithin ? (
            <ul className="mt-2 grid gap-2">
              {withinRows.map((r) => (
                <TerrainMarkerTile key={r.marker.key} row={r} />
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
      {problems.length ? (
        <ul className="mt-3 flex flex-col gap-1" aria-label="Problem list">
          {problems.map((p) => (
            <li key={p.name} className={cn(V2_BODY, 'text-text-primary')}>
              {p.name}
            </li>
          ))}
        </ul>
      ) : null}
      {summary.system.id === 'heart-vessels' ? <TerrainRiskInstruments system={summary} all={all} /> : null}
    </section>
  );
}

/** The demo's radar legend: two rows of swatch + word, token classes only. */
function TerrainRadarLegend({ hasPrevious }: { hasPrevious: boolean }) {
  const item = 'inline-flex items-center gap-2';
  return (
    <div className={cn(V2_META, 'flex flex-col items-center gap-1', SECONDARY)} data-slot="terrain-radar-legend">
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
        <span className={item}>
          <span aria-hidden className="h-0.5 w-4 rounded-full bg-brand-primary" />
          Now · in target
        </span>
        {hasPrevious ? (
          <span className={item}>
            <span aria-hidden className="w-4 border-t border-dashed border-text-muted" />
            Previous draw
          </span>
        ) : null}
        <span className={item}>
          <span aria-hidden className="h-2.5 w-4 rounded-sm border border-green-600 bg-green-200" />
          Clinic optimal (clinic policy) · none
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
        <span className={item}>
          <span aria-hidden className="h-2.5 w-4 rounded-sm border border-blue-600 bg-blue-200" />
          Clinical target
        </span>
        <span className={item}>
          <span aria-hidden className="h-2.5 w-4 rounded-sm border border-yellow-700 bg-yellow-200" />
          Outside target
        </span>
        <span className={item}>Rings · 25–100%</span>
      </div>
    </div>
  );
}

export function TerrainView({ systems, conditions, isLoading, isPartial }: TerrainViewProps) {
  const [mode, setMode] = useState<'radar' | 'matrix'>('radar');
  const firstAttention = systems.find((s) => s.state === 'attention') ?? systems.find((s) => s.assessed > 0) ?? systems[0];
  const [selectedId, setSelectedId] = useState<TerrainSystemId | null>(null);
  const selected = systems.find((s) => s.system.id === selectedId) ?? firstAttention;
  const onRadar = useMemo(() => systems.filter((s) => s.assessed > 0), [systems]);
  const offRadar = systems.filter((s) => s.assessed === 0);
  const attention = attentionCount(systems);
  const hasPrevious = onRadar.length > 2 && onRadar.every((s) => s.previousAssessed > 0);

  if (isLoading) {
    return (
      <p className={cn(V2_META, 'p-6')} data-slot="terrain-loading">
        Reading the record…
      </p>
    );
  }
  return (
    <div data-slot="terrain" className="grid content-start gap-4 p-6 lg:grid-cols-5">
      <section className={cn(CARD, 'flex flex-col gap-3 lg:col-span-3')}>
        <div className="self-start">
          <Segmented
            aria-label="Terrain view"
            options={[
              { label: 'Radar', value: 'radar' },
              { label: 'Matrix', value: 'matrix' },
            ]}
            value={mode}
            onChange={(v: string) => setMode(v === 'matrix' ? 'matrix' : 'radar')}
          />
        </div>
        {isPartial ? <p className={cn(V2_META, SECONDARY)}>Some results could not be read. What is shown is what loaded.</p> : null}
        {mode === 'matrix' ? (
          <TerrainMatrix selected={selected?.system.id ?? null} />
        ) : (
          <>
            <header className="flex min-h-8 flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <h2 className={V2_CARD_TITLE}>
                {attention} of {systems.length} systems need attention
              </h2>
              <span className={cn(V2_META, 'min-w-0 truncate', SECONDARY)} title="share of assessed markers within their target or threshold · a count, not a score">
                share of assessed markers within band · a count, not a score
              </span>
            </header>
            {onRadar.length >= 1 ? (
              <>
                <TerrainRadarChart
                  systems={systems}
                  selected={selected?.system.id ?? null}
                  onSelect={setSelectedId}
                  ariaLabel={`Body systems: ${onRadar.map((s) => `${s.system.short} ${s.assessedWithin} of ${s.assessed} within band`).join(', ')}`}
                />
                <TerrainRadarLegend hasPrevious={hasPrevious} />
              </>
            ) : (
              <p className={cn(V2_META, SECONDARY)} data-slot="terrain-radar-empty">
                No body system has a result compared against a target or threshold yet.
              </p>
            )}
            {/* The radar's axis names select a system with a pointer; this list is the same switch for the keyboard and screen readers. */}
            <div
              className={cn(
                onRadar.length >= 1
                  ? 'sr-only focus-within:not-sr-only focus-within:flex focus-within:flex-wrap focus-within:gap-x-3 focus-within:gap-y-1'
                  : 'flex flex-wrap gap-x-3 gap-y-1',
                V2_META,
              )}
              role="group"
              aria-label="Body systems"
            >
              {onRadar.map((s) => (
                <button
                  key={s.system.id}
                  type="button"
                  data-system-select={s.system.id}
                  aria-pressed={selected?.system.id === s.system.id}
                  onClick={() => setSelectedId(s.system.id)}
                  className={cn(LINK, 'underline')}
                >
                  {s.system.short} · {s.assessedWithin} of {s.assessed}
                </button>
              ))}
            </div>
            {offRadar.length ? (
              <p className={cn(V2_META, 'text-center', SECONDARY)}>
                Not in the radar:{' '}
                {offRadar.map((s, i) => (
                  <span key={s.system.id}>
                    {i > 0 ? ', ' : ''}
                    <button
                      type="button"
                      data-system-select={s.system.id}
                      aria-pressed={selected?.system.id === s.system.id}
                      onClick={() => setSelectedId(s.system.id)}
                      className={cn('underline', selected?.system.id === s.system.id ? 'text-brand-primary' : 'text-text-primary')}
                    >
                      {s.system.short}
                    </button>
                  </span>
                ))}
              </p>
            ) : null}
          </>
        )}
      </section>
      {selected ? (
        <div className="lg:col-span-2">
          <TerrainSystemPanel key={selected.system.id} summary={selected} all={systems} conditions={conditions} />
        </div>
      ) : null}
    </div>
  );
}
